import { fetchJSON, fetchText } from '../utilities/fetch';
import { SERVICE_CLIENT_ID } from '../settings';
import { objectToQuery } from '../utilities/url';

interface BatchGetStartingAtPricePresentationBySKUResponse {
  Presentations: {
    [sku: string]: {
      QtyStartingAtPrice: { [quantity: string]: string };
    };
  };
}
export async function batchGetStartingAtPricePresentationBySKU(
  skus: string[],
  quantity: number,
  pricingContext: string,
) {
  const query = {
    clientId: SERVICE_CLIENT_ID,
    context: pricingContext,
    Types: 'QtyStartingAtPrice',
    Ids: skus,
    Qtys: quantity.toString(),
  };

  const url = `https://product.pricing.vpsvc.com/v4/skus/prices/presentation/${objectToQuery(
    query,
  )}`;

  const responseJSON = await fetchJSON<BatchGetStartingAtPricePresentationBySKUResponse>(url, {
    description: 'batch get price presentation by SKU',
  });

  return Object.keys(responseJSON.Presentations).reduce<{ [sku: string]: string }>((acc, sku) => {
    acc[sku] = responseJSON.Presentations[sku].QtyStartingAtPrice[quantity];
    return acc;
  }, {});
}

interface GetStartingAtPricePresentationBySKUResponse {
  Presentations: {
    QtyStartingAtPrice: { [quantity: string]: string };
  };
}
export async function getStartingAtPricePresentationBySKU(
  sku: string,
  quantity: number,
  pricingContext: string,
) {
  const query = {
    clientId: SERVICE_CLIENT_ID,
    context: pricingContext,
    Types: 'QtyStartingAtPrice',
    Qtys: quantity.toString(),
  };

  const url = `https://product.pricing.vpsvc.com/v4/skus/${sku}/prices/presentation${objectToQuery(
    query,
  )}`;

  const responseJSON = await fetchJSON<GetStartingAtPricePresentationBySKUResponse>(url, {
    description: `get price presentation by SKU: ${sku}`,
  });

  return responseJSON.Presentations.QtyStartingAtPrice[quantity];
}

interface BatchGetUpsellPricePresentationBySKUResponse {
  SkuDiffPresentations: {
    [sku: string]: {
      PlusOrMinus: string;
    };
  };
}
export async function batchGetUpsellPricePresentationBySKU(
  skus: string[],
  quantity: number,
  pricingContext: string,
) {
  const query = {
    clientId: SERVICE_CLIENT_ID,
    context: pricingContext,
    Types: 'PlusOrMinus',
    Quantity: quantity.toString(),
    Skus: skus,
  };

  const url = `https://product.pricing.vpsvc.com/v4/skus/prices/presentations/diff/upsell/${objectToQuery(
    query,
  )}`;

  const responseJSON = await fetchJSON<BatchGetUpsellPricePresentationBySKUResponse>(url, {
    description: 'batch get upsell price presentation by SKU',
  });

  return Object.keys(responseJSON.SkuDiffPresentations).reduce<{ [sku: string]: string }>(
    (acc, sku) => {
      acc[sku] = responseJSON.SkuDiffPresentations[sku].PlusOrMinus;
      return acc;
    },
    {},
  );
}

interface BatchGetPriceBySKUResponse {
  Currency: string;
  Products: {
    Id: string;
    QtyPrices: {
      Qty: number;
      ListUntaxed: number;
      ListTaxed: number;
      ListUntaxedUnrounded: number;
      ListTaxedUnrounded: number;
      DiscountUntaxed: number;
      DiscountTaxed: number;
      DiscountUntaxedUnrounded: number;
      DiscountTaxedUnrounded: number;
    }[];
  }[];
}
export async function batchGetPriceBySKU(
  skus: string[],
  pricingContext: string,
  options: { quantity?: number } = {},
) {
  const { quantity } = options;

  const query = {
    clientId: SERVICE_CLIENT_ID,
    context: pricingContext,
    DisplayType: 'TotalPrice',
    Qtys: quantity !== undefined ? quantity.toString() : undefined,
    Ids: skus,
  };

  const url = `https://product.pricing.vpsvc.com/v4/skus/prices${objectToQuery(query)}`;

  const responseJSON = await fetchJSON<BatchGetPriceBySKUResponse>(url, {
    description: 'batch get price by SKU',
  });

  if (responseJSON.Products.length === 0) {
    throw new Error(`No prices found for SKU: ${skus.join(',')}`);
  }

  return responseJSON.Products.reduce<{ [sku: string]: { quantity: number; price: number } }>(
    (acc, product) => {
      if (product.QtyPrices.length === 0) {
        throw new Error(`No price found for SKU: ${product.Id}`);
      }

      acc[product.Id] = {
        quantity: product.QtyPrices[0].Qty,
        // we'll use the untaxed list price as our notion of "Price" (so that we get the proper "base sku" when sorting by price)
        // using a discounted price could result in a premium option being used as the base sku (if the premium option had a heavy discount)
        // todo: is this the right call? In the case of a heavily-discounted premium option, we may see negative upsell pricing...
        price: product.QtyPrices[0].ListUntaxed,
      };
      return acc;
    },
    {},
  );
}

export interface Offer {
  sku: string;
  quantity: number;
  presentationHTML: string;
}

export async function getBaseOfferBySKUs(
  skus: string[],
  pricingContext: string,
  options: { quantity?: number } = {},
): Promise<Offer> {
  const { quantity } = options;

  const pricesBySKU = await batchGetPriceBySKU(skus, pricingContext, { quantity });
  const baseSKU = Object.keys(pricesBySKU).reduce(
    (minPriceSKU, sku) =>
      pricesBySKU[sku].price < pricesBySKU[minPriceSKU].price ? sku : minPriceSKU,
    Object.keys(pricesBySKU)[0], // initial value for minPriceSKU is the first SKU
  );
  const baseQuantity = pricesBySKU[baseSKU].quantity;

  const baseSKUStartingPricePresentation = await getStartingAtPricePresentationBySKU(
    baseSKU,
    baseQuantity,
    pricingContext,
  );

  return {
    sku: baseSKU,
    quantity: baseQuantity,
    presentationHTML: baseSKUStartingPricePresentation,
  };
}

export async function getPricingContext(
  culture: string,
  countryCode: string,
  options: { couponCode?: string; includeTax?: boolean } = {},
) {
  const { couponCode, includeTax = false } = options;

  const query = {
    Culture: culture,
    CountryCode: countryCode,
    CouponCode: couponCode,
    ShowTaxInclusive: includeTax.toString(),
  };

  return fetchText(
    `https://context.pricing.vpsvc.com/v1/pricingcontext/id/${objectToQuery(query)}`,
    { description: 'get pricing context' },
  );
}
